import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CountryService } from '@/app/services/shared/country';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { Router } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete'; // Import MatAutocompleteModule

@Component({
  selector: 'app-country',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatAutocompleteModule,
  ],
  templateUrl: './country.component.html',
  styleUrls: ['./country.component.css'], // Fixed typo from "styleUrl" to "styleUrls"
})
export class CountryComponent implements OnInit, OnChanges {
  @Input() country: string | null = null;
  @Input() isRequired: boolean = false;
  @Output() changeCountry: EventEmitter<any> = new EventEmitter<any>();

  countries: any[] = [];
  filteredCountries: any[] = [];
  selectedCountry: any = null;

  defaultSelectedCountry = {
    id: 0,
    name: '',
    code: '',
    currency: '',
    symbol: '',
    flag: '',
  };

  searchTermControl = new FormControl();
  countryControl = new FormControl(null);

  dropdownOpen = false;

  constructor(
    private countryServices: CountryService,
    private router: Router,
    private projectsService: ProjectsService,
    private eRef: ElementRef
  ) {}

  ngOnInit(): void {
    // Setup search term filtering
    this.searchTermControl.valueChanges.subscribe((value: string) => {
      const searchTerm = value?.toLowerCase() || '';
      this.filteredCountries = this.countries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm)
      );
    });

    // Set validators for countryControl
    this.countryControl.setValidators(
      this.isRequired ? Validators.required : null
    );

    // Fetch the country list and handle selection
    this.getCountriesList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update validators if isRequired changes
    if (changes['isRequired']) {
      this.countryControl.setValidators(
        this.isRequired ? Validators.required : null
      );
      this.countryControl.updateValueAndValidity();
    }

    // Handle input country changes
    if (changes['country'] && this.countries.length > 0) {
      this.setCountryFromInput();
    }
  }

  getCountriesList(): void {
    this.projectsService.getCountries().subscribe({
      next: (response: any) => {
        this.countries = response.data || [];
        this.filteredCountries = [...this.countries];

        // Handle initial country selection
        this.setCountryFromInput();
      },
      error: (error) => {
        console.error('API error:', error);
      },
    });
  }

  setCountryFromInput(): void {
    if (this.country) {
      const inputCountry = this.countries.find(
        (c) => c.name.toLowerCase() === this.country.toLowerCase()
      );

      if (inputCountry) {
        this.selectedCountry = inputCountry;
      } else {
        console.warn(
          'Provided country input not found in the list of countries.'
        );
        this.selectedCountry = this.defaultSelectedCountry;
      }
    } else {
      this.selectedCountry = this.defaultSelectedCountry;
    }

    // Update country control and emit the selected country
    this.countryControl.setValue(this.selectedCountry);
    this.changeCountry.emit(this.selectedCountry);
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  onCountrySelect(country: any): void {
    this.selectedCountry = country;
    this.countryControl.setValue(country); // Set the selected country value
    this.countryControl.markAsTouched(); // Mark control as touched
    this.changeCountry.emit(country); // Emit the selected country
    this.dropdownOpen = false; // Close the dropdown
    this.searchTermControl.setValue(''); // Reset the search term
    this.filteredCountries = [...this.countries];
    this.countryServices.setCountries(this.selectedCountry);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }
}
